import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Copy from 'lucide-react/dist/esm/icons/copy';
import Check from 'lucide-react/dist/esm/icons/check';
import CircleCheck from 'lucide-react/dist/esm/icons/circle-check';
import {
	Dialog, DialogContent
} from '~/components/ui/dialog';
import Button from '~/modules/components/global/button';

const CopyField = ({ value, 'data-testid': testId, 'data-copy-testid': copyTestId }) => {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(value);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2.5">
			<code className="flex-1 truncate font-mono text-sm" data-testid={testId}>
				{value}
			</code>
			<button
				type="button"
				onClick={handleCopy}
				data-testid={copyTestId}
				aria-label="Copy to clipboard"
				className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none"
			>
				{copied
					? <Check className="h-4 w-4 text-green-400" />
					: <Copy className="h-4 w-4" />
				}
			</button>
		</div>
	);
};

CopyField.propTypes = {
	value: PropTypes.string.isRequired,
	'data-testid': PropTypes.string,
	'data-copy-testid': PropTypes.string
};

const ApiTokenRevealModal = ({
	token,
	onClose,
	title = 'API token created',
	// Defaults describe a personal token. A service account token acts as the account, not as the
	// person who minted it, so callers override this rather than showing misleading copy.
	scopeWarning = 'Anyone with this value can act as you on the API.'
}) => {
	const headerValue = token?.token ? `Bearer ${token.token}` : '';

	return (
		<Dialog open={!!token} onOpenChange={(open) => {
			if(!open) {
				onClose();
			}
		}}
		>
			<DialogContent
				className="rounded-xl p-0 overflow-hidden"
				style={{ maxWidth: '38rem' }}
				onInteractOutside={(e) => e.preventDefault()}
			>
				<div className="p-6 space-y-4">
					<div className="flex items-start gap-3">
						<CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-400" />
						<div>
							<p className="font-semibold leading-tight">{title}</p>
							<p className="mt-0.5 text-sm text-muted-foreground">
								{token?.name && <><span className="font-medium text-foreground">{token.name}</span>{' '}</>}
								is ready. Copy it now — this is the only time it will be shown.
								{' '}{scopeWarning}
							</p>
						</div>
					</div>

					<CopyField
						value={token?.token || ''}
						data-testid="api-token-revealed-value"
						data-copy-testid="api-token-copy-button"
					/>

					<div className="space-y-1.5">
						<p className="text-xs text-muted-foreground">Authorization header</p>
						<CopyField
							value={headerValue}
							data-testid="api-token-header-value"
							data-copy-testid="api-token-header-copy-button"
						/>
					</div>
				</div>

				<div className="flex justify-end border-t border-border px-6 py-4">
					<Button
						variant="secondary"
						onClick={onClose}
						data-testid="api-token-reveal-close-button"
					>
						Done
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

ApiTokenRevealModal.propTypes = {
	token: PropTypes.shape({
		_id: PropTypes.string,
		name: PropTypes.string,
		token: PropTypes.string
	}),
	onClose: PropTypes.func.isRequired,
	title: PropTypes.string,
	scopeWarning: PropTypes.string
};

export default ApiTokenRevealModal;
